import TopBar from "@/components/top-bar";
import Icon from "@/components/icon";
import Section from "@/components/section";
import Row, { RowDivider } from "@/components/row";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { openUrl, parseLinksAndMentions } from "@/utils/utilities";
import {
  EITAA_BASE_URL,
  SUPPORT_EITAA_USERNAME,
  SUPPORT_PHONE,
} from "@/utils/constant";
import { SUPPORT_FAQ } from "./constants";

const Chevron = () => (
  <Icon name="chevron-left" size={18} className="text-default-300" />
);

/** Shared by the accordion items and the link rows, so both sit on one list. */
const faqItemClass = "border-b border-default-100 last:border-b-0";
const faqTriggerClass =
  "flex w-full items-center justify-between gap-2 py-3 text-right text-sm font-medium text-default-900";

/**
 * Support hub: the two direct contact channels, then the FAQ. Most questions
 * expand in place — the same accordion the course page uses for its own FAQ —
 * while a question carrying a `url` opens that post instead. See
 * `docs/telegram-ui.md` for the row/section language used here.
 */
export default function SupportPage() {
  const supportUsername = SUPPORT_EITAA_USERNAME.replace(/^@/, "");

  const openSupportChat = () => openUrl(`${EITAA_BASE_URL}/${supportUsername}`);

  const callSupport = () => {
    window.location.href = `tel:${SUPPORT_PHONE.replace(/[^\d+]/g, "")}`;
  };

  return (
    <div className="content-container min-h-svh bg-secondary-background pb-10">
      <TopBar title="پشتیبانی" />

      <Section title="ارتباط مستقیم">
        <Row
          icon="support"
          iconClassName="bg-primary"
          title="ارسال پیام به پشتیبانی"
          subtitle={`@${supportUsername}`}
          trailing={<Chevron />}
          onClick={openSupportChat}
        />
        <RowDivider inset />
        <Row
          icon="support-phone"
          iconClassName="bg-green-500"
          title="تماس با پشتیبانی"
          subtitle={<span dir="ltr">{SUPPORT_PHONE}</span>}
          trailing={<Chevron />}
          onClick={callSupport}
        />
      </Section>

      <Section title="سوالات متداول">
        <Accordion type="single" collapsible className="px-4 pb-2" dir="rtl">
          {SUPPORT_FAQ.map((item) =>
            item.url ? (
              <div key={item.id} className={faqItemClass}>
                <button
                  type="button"
                  onClick={() => openUrl(item.url)}
                  className={`${faqTriggerClass} outline-none transition active:opacity-70 focus-visible:ring-2 focus-visible:ring-primary`}
                >
                  <span className="min-w-0 flex-1 text-right leading-6">
                    {item.question}
                  </span>
                  <Chevron />
                </button>
              </div>
            ) : (
              <AccordionItem key={item.id} value={item.id}>
                <AccordionTrigger className="text-default-900">
                  <span className="min-w-0 flex-1 text-right leading-6">
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  {/* Turns the @support handles in the answers into links that
                      open in Eitaa, the same way comments and course
                      descriptions already render mentions. */}
                  <p className="whitespace-pre-line text-xs leading-loose text-default-700">
                    {parseLinksAndMentions(item.answer ?? "")}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ),
          )}
        </Accordion>
      </Section>
    </div>
  );
}
